import { type IAttachmentRepository } from '@webapp/core/business-logic/interfaces/IAttachmentRepository'
import { type AttachmentApiModel } from '@webapp/core/data-access/api-models/AttachmentApiModel'
import { mapAttachmentApiModel } from '@webapp/core/data-access/mappers/mapAttachment'
import { type UploadAttachmentPayload } from '@webapp/core/data-access/types/UploadAttachmentPayload'
import { type AttachmentConstructorData } from '@webapp/core/entities/internal/Attachment'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import type { RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'

interface Dependency {
  httpClient: IHttpClient
}

export const ATTACHMENTS_BASE_URL = '/api/v3/attachments/'

export class AttachmentRepository implements IAttachmentRepository {
  constructor(private readonly deps: Dependency) {}

  async upload(
    data: UploadAttachmentPayload
  ): Promise<AttachmentConstructorData> {
    const res = await this.deps.httpClient.post<
      RepoStandardResponse<AttachmentApiModel>
    >(ATTACHMENTS_BASE_URL, data)

    const attachmentData = res.data.data

    return mapAttachmentApiModel(attachmentData)
  }

  async updateAttachment(
    uuid: uuid,
    data: { name: string }
  ): Promise<RepoStandardResponse<AttachmentConstructorData>> {
    try {
      const res = await this.deps.httpClient.patch<
        RepoStandardResponse<AttachmentApiModel>
      >(`${ATTACHMENTS_BASE_URL}${uuid}/`, data)

      const attachmentData = res.data.data

      return {
        data: mapAttachmentApiModel(attachmentData),
      }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async deleteAttachment(uuid: uuid): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${ATTACHMENTS_BASE_URL}${uuid}/`)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }
}
